import React, { useContext, useEffect, useState } from 'react'
import { Body1 } from '@/components/ui/typography'
import { TradingAccountDetailDto } from 'api/Clients/Types/TradingAccountDetails'
import { UpdateTradingAccount } from 'api/Clients/Types/UpdateTradingAccount'
import { TradingAccountBalanceDisabledException } from 'api/exceptions'
import { BookTypeID, BookTypeIdToFriendlyName } from 'api/Trading-Accounts/Types/BookType'
import {
  MarketingCampaignDto,
  MarketingCampaignFilterKey,
} from 'api/Trading-Accounts/Types/MarketingCampaignDto'
import { MarketingCampaignStatus } from 'api/Trading-Accounts/Types/MarketingCampaignStatus'
import { PlatformStatisticsDto } from 'api/Trading-Accounts/Types/PlatformStatisticsDto'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import TwoStepConfirmationModal from 'components/ConfirmationModal/TwoStepConfirmationModal'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { useAppDispatch, useAppSelector } from 'store'
import { initialState } from 'store/grid'
import { fetchIntroducingBrokerByTickmillCompany } from 'store/partnerships/partnerships.actions'
import {
  fetchTradingAccountGroupsByTradingAccountAction,
  fetchTradingAccountLeverageTypesForPlatformsAction,
} from 'store/trading-accounts/trading-accounts.actions'
import { GridFilterOperatorEnum } from 'types/GridFilterOperatorEnum'
import { useApiClient } from 'utils/api/ApiClient'
import { PartnershipsApiClient } from 'utils/api/partnershipsApi'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { ConfirmationContext } from 'utils/ConfirmationContext'
import { buildQuery, FilterLogic } from 'utils/QueryHelpers'
import { uniqueArrayOfObjectById } from 'utils/unique/unique'

import { LiveTradingAccountNavigation } from '../Navigation/LiveTradingAccountNavigation'
import { TradingAccountTab } from '../Navigation/TradingAccountNavigation'
import { TradingAccountDetailsForm } from './TradingAccountDetailsForm'
import TradingAccountDetailsPage, { TradingAccountDetailsProps } from './TradingAccountDetailsPage'

interface Props extends TradingAccountDetailsProps {
  data: TradingAccountDetailDto

  forceRefresh(): void
}

const LiveTradingAccountDetailsPage: React.FC<Props> = ({
  data,
  isEdit,
  setEdit,
  forceRefresh,
  ...props
}) => {
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const partnershipsApiClient = useApiClient(PartnershipsApiClient)

  const dispatch = useAppDispatch()

  const setToast = useContext(ToastContext)
  const [requireConfirmation, setRequireConfirmation] = useContext(ConfirmationContext)

  const [platformStatistics, setPlatformStatistics] = useState<PlatformStatisticsDto | null>(null)
  const [confirmationData, setConfirmationData] = useState<UpdateTradingAccount | null>(null)
  const [marketingCampaigns, setMarketingCampaigns] = useState<MarketingCampaignDto[]>([])
  const [isChangeBookTypeModalConfirmation, setIsChangeBookTypeModalConfirmation] = useState<{
    visible: boolean
    numberOfSteps: number
    oldBookType?: BookTypeID
    newBookType?: BookTypeID
    oldLeverageTypeId?: number
    newLeverageTypeId?: number
    values?: UpdateTradingAccount
  }>({ visible: false, numberOfSteps: 1 })

  const tradingAccountGroupsByTradingAccount = useAppSelector(
    (state) => state.tradingAccounts.tradingAccountGroupsByTradingAccount
  )
  const introducingBrokers = useAppSelector(
    (state) => state.partnerships.introducingBrokersByCompany[data.tickmillCompany.id] || []
  )
  console.log(data.tickmillCompany.id, 'id')
  console.log(introducingBrokers, 'ib')

  const leverageType = useAppSelector((state) => state.tradingAccounts.leverageForPlatforms) ?? []

  useEffect(() => {
    ;(async () => {
      dispatch(
        fetchIntroducingBrokerByTickmillCompany(partnershipsApiClient, data.tickmillCompany.id)
      )
      dispatch(fetchTradingAccountGroupsByTradingAccountAction(tradingAccountsApiClient, data.id))
      dispatch(
        fetchTradingAccountLeverageTypesForPlatformsAction(
          data.client.id,
          tradingAccountsApiClient,
          data.accountGroup.platformType.id
        )
      )

      const campaigns = await tradingAccountsApiClient.getMarketingCampaigns({
        search: createCampaignsQuery(),
        pageIndex: 1,
        pageSize: 1000,
      })
      setMarketingCampaigns(campaigns.items)
    })()
  }, [data.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const updateTradingAccount = async (values: UpdateTradingAccount) => {
    try {
      setRequireConfirmation({ payload: values })
      await tradingAccountsApiClient.updateTradingAccount(data.id, values)
      setToast(successToast('Trading Account updated.'))
      forceRefresh()
    } catch (e) {
      console.error(e)
      setConfirmationData(null)
      setIsChangeBookTypeModalConfirmation({
        visible: false,
        newBookType: undefined,
        values: undefined,
        newLeverageTypeId: undefined,
        oldLeverageTypeId: undefined,
        oldBookType: undefined,
        numberOfSteps: 1,
      })
    }
  }
  const handleConfirmUpdateTradingAccount = async () => {
    if (requireConfirmation?.payload) {
      await tradingAccountsApiClient.updateTradingAccount(data.id, {
        ...(requireConfirmation?.payload as UpdateTradingAccount),
        ignoreTradingAccountTypeChangeWarning: true,
      })
      setToast(successToast('Trading Account updated.'))
      setRequireConfirmation(null)
      forceRefresh()
    }
  }

  const showConfirmationModal = async (
    values: UpdateTradingAccount,
    numberOfSteps: number,
    oldBookType?: BookTypeID,
    newBookType?: BookTypeID,
    oldLeverageTypeId?: number,
    newLeverageTypeId?: number
  ) => {
    if (
      newBookType === BookTypeID.A ||
      newBookType === BookTypeID.B ||
      oldLeverageTypeId ||
      newLeverageTypeId
    ) {
      return setIsChangeBookTypeModalConfirmation({
        visible: true,
        numberOfSteps,
        oldBookType,
        newBookType,
        oldLeverageTypeId,
        newLeverageTypeId,
        values,
      })
    }

    return updateTradingAccount(values)
  }

  const onSubmit = async (values: UpdateTradingAccount) => {
    const newAccountGroup = getTradingAccountGroupsByTradingAccount().find(
      (item) => item.id === values.accountGroupId
    )
    const isChangingBookType = data?.accountGroup?.bookType?.id !== newAccountGroup?.bookType?.id
    const isChangingLeverage = data?.leverageType.id !== values.leverageTypeId
    if (isChangingBookType && isChangingLeverage) {
      return await showConfirmationModal(
        values,
        2,
        data.accountGroup.bookType?.id,
        newAccountGroup?.bookType?.id,
        data.leverageType.id,
        values.leverageTypeId
      )
    }
    if (isChangingBookType) {
      return await showConfirmationModal(
        values,
        1,
        data.accountGroup.bookType?.id,
        newAccountGroup?.bookType?.id
      )
    }
    if (isChangingLeverage) {
      const isLeverageEnabled = leverageType.find(
        (l) => l.id === Number(values.leverageTypeId)
      )?.enabled
      if (!isLeverageEnabled) {
        return await showConfirmationModal(
          values,
          1,
          undefined,
          undefined,
          data.leverageType.id,
          values.leverageTypeId
        )
      }
    }
    try {
      await updateTradingAccount(values)
    } catch (error) {
      if (error instanceof TradingAccountBalanceDisabledException) {
        await handleConfirmationOpenModal(values)
      } else {
        setIsChangeBookTypeModalConfirmation({
          visible: false,
          newBookType: undefined,
          values: undefined,
          newLeverageTypeId: undefined,
          oldLeverageTypeId: undefined,
          oldBookType: undefined,
          numberOfSteps: 1,
        })
        throw error
      }
    }
  }

  const getTradingAccountGroupsByTradingAccount = () => {
    const tradingAccountsWithInitialValue = tradingAccountGroupsByTradingAccount.concat({
      id: data.accountGroup.id,
      name: data.accountGroup.name,
      currency: data.accountGroup.currency,
      bookType: data.accountGroup.bookType,
    })

    return uniqueArrayOfObjectById(tradingAccountsWithInitialValue)
  }

  const handleConfirmationSubmit = async () => {
    if (confirmationData) {
      await updateTradingAccount({
        ...confirmationData,
        ignoreWarnings: true,
      })
      setConfirmationData(null)
    }
  }

  const handleConfirmationOpenModal = async (values: UpdateTradingAccount) => {
    const tradingAccountStatistics =
      await tradingAccountsApiClient.getTradingAccountStatisticsByTradingAccountId(data.id)
    setPlatformStatistics(tradingAccountStatistics)
    setConfirmationData(values)
  }

  const handleConfirmationCloseModal = () => {
    setEdit(true)
    setConfirmationData(null)
  }

  return (
    <React.Fragment>
      {requireConfirmation?.code && (
        <ConfirmationModal
          yesButtonText='Yes, process'
          onYes={handleConfirmUpdateTradingAccount}
          onCancel={() => setRequireConfirmation(null)}
          visible={!!requireConfirmation.code}
          renderTitle={
            <div>
              <p>
                Client Does Not Meet the minimum requirements
                <br />
                for VIP status, would you like to proceed anyway?
              </p>
            </div>
          }
        />
      )}
      {!!confirmationData && (
        <ConfirmationModal
          yesButtonText='Proceed'
          onYes={handleConfirmationSubmit}
          onCancel={handleConfirmationCloseModal}
          visible={!!confirmationData}
          renderTitle={
            <div>
              <p>
                The trading account {data?.name} has a balance {platformStatistics?.balance}{' '}
                {platformStatistics?.currency.id}. Do you want to proceed with disabling this
                account?
              </p>
            </div>
          }
        />
      )}
      {isChangeBookTypeModalConfirmation.visible &&
        isChangeBookTypeModalConfirmation.values &&
        (isChangeBookTypeModalConfirmation.numberOfSteps === 1 ? (
          <ConfirmationModal
            renderTitle={
              <>
                {isChangeBookTypeModalConfirmation.newBookType ? (
                  <Body1>
                    {`You have requested to change the Trading Account Group between different book categories 
                            (From ${
                              isChangeBookTypeModalConfirmation.oldBookType
                                ? BookTypeIdToFriendlyName[
                                    isChangeBookTypeModalConfirmation.oldBookType
                                  ]
                                : 'N/A'
                            } 
                            book to ${
                              isChangeBookTypeModalConfirmation.newBookType
                                ? BookTypeIdToFriendlyName[
                                    isChangeBookTypeModalConfirmation.newBookType
                                  ]
                                : 'N/A'
                            } book). 
                            Please confirm your action.`}
                  </Body1>
                ) : (
                  <Body1>
                    {`You have requested to change the Leverage to an option we no longer offer in Client Area/Website.  
                        Please confirm your action.`}
                  </Body1>
                )}
              </>
            }
            yesButtonText='Proceed'
            onYes={() =>
              updateTradingAccount(isChangeBookTypeModalConfirmation.values as UpdateTradingAccount)
            }
            onCancel={() =>
              setIsChangeBookTypeModalConfirmation({
                visible: false,
                numberOfSteps: 1,
                oldBookType: undefined,
                newBookType: undefined,
                oldLeverageTypeId: undefined,
                newLeverageTypeId: undefined,
                values: undefined,
              })
            }
            visible={isChangeBookTypeModalConfirmation.visible}
          />
        ) : (
          <TwoStepConfirmationModal
            visible={isChangeBookTypeModalConfirmation.visible}
            onYes={({ confirmedStep1, confirmedStep2 }) => {
              if (confirmedStep1 && confirmedStep2) {
                updateTradingAccount(
                  isChangeBookTypeModalConfirmation.values as UpdateTradingAccount
                )
              } else if (confirmedStep1) {
                updateTradingAccount({
                  ...(isChangeBookTypeModalConfirmation.values as UpdateTradingAccount),
                  leverageTypeId: data.leverageType.id,
                })
              } else if (confirmedStep2) {
                updateTradingAccount({
                  ...(isChangeBookTypeModalConfirmation.values as UpdateTradingAccount),
                  accountGroupId: data.accountGroup.id,
                })
              }
            }}
            onCancel={({ confirmedStep1, confirmedStep2 }) => {
              if (!confirmedStep2 && !confirmedStep1) {
                return setIsChangeBookTypeModalConfirmation({
                  visible: false,
                  numberOfSteps: 1,
                  oldBookType: undefined,
                  newBookType: undefined,
                  oldLeverageTypeId: undefined,
                  newLeverageTypeId: undefined,
                  values: undefined,
                })
              }
              if (confirmedStep1) {
                updateTradingAccount({
                  ...(isChangeBookTypeModalConfirmation.values as UpdateTradingAccount),
                  leverageTypeId: data.leverageType.id,
                })
              }
            }}
            renderStep1Title={
              <>
                <Body1>
                  {`You have requested to change the Trading Account Group between different book categories 
                            (From ${
                              isChangeBookTypeModalConfirmation.oldBookType
                                ? BookTypeIdToFriendlyName[
                                    isChangeBookTypeModalConfirmation.oldBookType
                                  ]
                                : 'N/A'
                            } 
                            book to ${
                              isChangeBookTypeModalConfirmation.newBookType
                                ? BookTypeIdToFriendlyName[
                                    isChangeBookTypeModalConfirmation.newBookType
                                  ]
                                : 'N/A'
                            } book). 
                            Please confirm your action.`}
                </Body1>
              </>
            }
            renderStep2Title={
              <>
                <Body1>
                  {`You have requested to change the Leverage to an option we no longer offer in Client Area/Website.  
                        Please confirm your action.`}
                </Body1>
              </>
            }
          />
        ))}
      <LiveTradingAccountNavigation
        data={data}
        hideTabs={data.accountGroup.platformType.id === 3}
        isEdit={isEdit}
        tab={TradingAccountTab.Details}
      />
      <TradingAccountDetailsForm
        {...props}
        data={data}
        isEdit={isEdit}
        setEdit={setEdit}
        onSubmit={onSubmit}
        accountGroups={getTradingAccountGroupsByTradingAccount()}
        leverageType={leverageType}
        introducingBrokers={introducingBrokers}
        marketingCampaigns={marketingCampaigns}
        forceRefresh={forceRefresh}
      />
    </React.Fragment>
  )
}

const createCampaignsQuery = () => {
  const initialGridState = initialState()
  return (
    buildQuery({
      ...initialGridState.filter,
      logic: initialGridState.filter.logic,
      defaultFilters: {
        logic: FilterLogic.AND,
        filters: [
          {
            field: MarketingCampaignFilterKey.status,
            operator: GridFilterOperatorEnum.equalOperator,
            value: MarketingCampaignStatus.Enabled,
          },
          {
            field: MarketingCampaignFilterKey.to,
            operator: GridFilterOperatorEnum.afterOrEqualOperator,
            value: new Date().toDateString(),
          },
        ],
      },
    }) ?? null
  )
}

interface LiveTradingAccountDetailsProps {
  data: TradingAccountDetailDto

  forceRefresh(): void
}

const LiveTradingAccountDetailsPageComponent: React.FC<LiveTradingAccountDetailsProps> = ({
  data,
  forceRefresh,
}) => {
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  return (
    <TradingAccountDetailsPage
      data={data}
      type='tradingAccount'
      resetTradingAccountPassword={tradingAccountsApiClient.resetTradingAccountPassword}
      render={(props) => (
        <LiveTradingAccountDetailsPage {...props} data={data} forceRefresh={forceRefresh} />
      )}
    />
  )
}

export default LiveTradingAccountDetailsPageComponent
